import { useState, useRef, useCallback, useEffect } from 'react';
import startStockReport from '../../api/cars/startStockReport';
import getStockReportStatus from '../../api/cars/getStockReportStatus';
import downloadStockReport from '../../api/cars/downloadStockReport';
import alert from '../../helpers/alert';

const POLL_INTERVAL_MS = 1000;
const MAX_POLL_FAILURES = 5;

const buildFileName = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `Ð¡ÑÐ¾Ðº_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.xlsx`;
};

const saveBlob = (data) => {
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildFileName();
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const useStockReportJob = () => {
  const [phase, setPhase] = useState('idle');
  const [progress, setProgress] = useState({ processed: 0, total: 0, percent: 0 });
  const [errorMessage, setErrorMessage] = useState('');
  const [doneTotal, setDoneTotal] = useState(0);
  const [doneReportId, setDoneReportId] = useState(null);

  const pollTimerRef = useRef(null);
  const pollFailuresRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const failWith = useCallback(
    (message) => {
      stopPolling();
      setErrorMessage(message);
      setPhase('error');
    },
    [stopPolling]
  );

  const handleDone = useCallback(
    async (reportId, total) => {
      stopPolling();

      if (total === 0) {
        setDoneTotal(0);
        setPhase('empty');
        return;
      }

      setPhase('downloading');
      try {
        const response = await downloadStockReport(reportId);
        saveBlob(response.data);
        setDoneReportId(reportId);
        setDoneTotal(total);
        setPhase('success');
      } catch (err) {
        failWith('ÐÑÑÐµÑ ÑÑÐ¾ÑÐ¼Ð¸ÑÐ¾Ð²Ð°Ð½, Ð½Ð¾ ÑÐºÐ°ÑÐ°ÑÑ ÑÐ°Ð¹Ð» Ð½Ðµ ÑÐ´Ð°Ð»Ð¾ÑÑ. ÐÐ¾Ð¿ÑÐ¾Ð±ÑÐ¹ÑÐµ ÐµÑÐµ ÑÐ°Ð·.');
      }
    },
    [stopPolling, failWith]
  );

  const startPolling = useCallback(
    (reportId) => {
      pollFailuresRef.current = 0;

      const poll = async () => {
        try {
          const res = await getStockReportStatus(reportId);
          pollFailuresRef.current = 0;
          const { status, total, processed, percent, error } = res.data;
          setProgress({ processed, total, percent });

          if (status === 'done') {
            handleDone(reportId, total);
            return;
          }
          if (status === 'error') {
            failWith(error || 'ÐÑÐ¸Ð±ÐºÐ° Ð¿ÑÐ¸ ÑÐ¾ÑÐ¼Ð¸ÑÐ¾Ð²Ð°Ð½Ð¸Ð¸ Ð¾ÑÑÐµÑÐ°');
            return;
          }
        } catch (err) {
          pollFailuresRef.current += 1;
          if (err?.response?.status === 404) {
            failWith('ÐÑÑÐµÑ Ð½Ðµ Ð½Ð°Ð¹Ð´ÐµÐ½ Ð½Ð° ÑÐµÑÐ²ÐµÑÐµ. ÐÐ¾Ð¿ÑÐ¾Ð±ÑÐ¹ÑÐµ ÑÑÐ¾ÑÐ¼Ð¸ÑÐ¾Ð²Ð°ÑÑ Ð·Ð°Ð½Ð¾Ð²Ð¾.');
            return;
          }
          if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
            failWith('ÐÐ¾ÑÐµÑÑÐ½Ð° ÑÐ²ÑÐ·Ñ Ñ ÑÐµÑÐ²ÐµÑÐ¾Ð¼. ÐÐ¾Ð¿ÑÐ¾Ð±ÑÐ¹ÑÐµ ÑÑÐ¾ÑÐ¼Ð¸ÑÐ¾Ð²Ð°ÑÑ Ð¾ÑÑÐµÑ Ð·Ð°Ð½Ð¾Ð²Ð¾.');
            return;
          }
        }
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      };

      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    },
    [failWith, handleDone]
  );

  const start = useCallback(
    async (filters, simulateError = false) => {
      if (phase === 'running') return;

      setPhase('running');
      setProgress({ processed: 0, total: 0, percent: 0 });
      setErrorMessage('');
      setDoneReportId(null);

      try {
        const res = await startStockReport({ ...filters, simulateError });
        if (!res?.data?.reportId) {
          failWith('Ð¡ÐµÑÐ²ÐµÑ Ð½Ðµ Ð²ÐµÑÐ½ÑÐ» Ð¸Ð´ÐµÐ½ÑÐ¸ÑÐ¸ÐºÐ°ÑÐ¾Ñ Ð¾ÑÑÐµÑÐ°. ÐÐ¾Ð¿ÑÐ¾Ð±ÑÐ¹ÑÐµ ÐµÑÐµ ÑÐ°Ð·.');
          return;
        }
        startPolling(res.data.reportId);
      } catch (err) {
        failWith('ÐÐµ ÑÐ´Ð°Ð»Ð¾ÑÑ Ð·Ð°Ð¿ÑÑÑÐ¸ÑÑ ÑÐ¾ÑÐ¼Ð¸ÑÐ¾Ð²Ð°Ð½Ð¸Ðµ Ð¾ÑÑÐµÑÐ°. ÐÐ¾Ð¿ÑÐ¾Ð±ÑÐ¹ÑÐµ ÐµÑÐµ ÑÐ°Ð·.');
      }
    },
    [phase, failWith, startPolling]
  );

  const downloadAgain = useCallback(async () => {
    if (!doneReportId) return;
    try {
      const response = await downloadStockReport(doneReportId);
      saveBlob(response.data);
    } catch (err) {
      alert.error('Ð¤Ð°Ð¹Ð» ÑÑÑÐ°ÑÐµÐ». Ð¡ÑÐ¾ÑÐ¼Ð¸ÑÑÐ¹ÑÐµ Ð¾ÑÑÐµÑ Ð·Ð°Ð½Ð¾Ð²Ð¾.');
      setDoneReportId(null);
      setPhase('idle');
    }
  }, [doneReportId]);

  return { phase, progress, errorMessage, doneTotal, start, downloadAgain };
};

export default useStockReportJob;
